import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDocs } from 'firebase/firestore';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { essentialDocAttachmentsCollection } from '../constants/firestorePaths';
import { useProjectMembers } from '../hooks/useProjectMembership';
import { useToast } from '../contexts/ToastContext';
import {
  attachmentRefusal,
  describeHolding,
  type TmfAttachmentLike,
} from '../utils/tmfAttachments';
import { locatedInLabel, TMF_ZONES_VIEW } from '../utils/tmfChecklistView';
import {
  ArrowLeft,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  XCircle,
  Filter,
  RefreshCw,
    Building2,
  MapPin,
  Download,
  Loader2,
  Paperclip,
  Info
} from 'lucide-react';

interface EssentialDoc {
  id: string;
  /**
   * The stable checklist key. Absent on projects initialized before
   * 2026-08-24, which is an ordinary supported shape — `attachmentRefusal`
   * falls back to `name` for those.
   */
  templateId?: string;
  name: string;
  description: string;
  category: string;
  zone: string;
  locatedIn: string;
  status: string;
  notes?: string;
  completedDate?: { _seconds: number };
}

interface DocsResponse {
  documents: EssentialDoc[];
  stats: {
    total: number;
    required: number;
    inProgress: number;
    complete: number;
    notApplicable: number;
  };
}

const categoryLabels: Record<string, string> = {
  before_trial: "Before Trial Begins",
  during_trial: "During Trial Conduct",
  after_trial: "After Trial Completion"
};

/**
 * Zone ids to zone names, derived from the one ported copy of the server's list
 * rather than typed out a second time here.
 *
 * This page and the Documents & TMF step each held their own copy until
 * 2026-08-25, and the other copy had diverged in every field it had — see
 * `utils/tmfChecklistView.ts`. The list is identical to what this map held, in
 * the same order, so the filter dropdown below is unchanged.
 */
const zoneLabels: Record<string, string> = Object.fromEntries(
  TMF_ZONES_VIEW.map(z => [z.id, z.name]),
);

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: JSX.Element }> = {
  required: { label: "Not Started", color: "text-red-700", bg: "bg-red-100", icon: <AlertCircle className="w-4 h-4" /> },
  in_progress: { label: "In Progress", color: "text-yellow-700", bg: "bg-yellow-100", icon: <Clock className="w-4 h-4" /> },
  complete: { label: "Complete", color: "text-green-700", bg: "bg-green-100", icon: <CheckCircle2 className="w-4 h-4" /> },
  not_applicable: { label: "N/A", color: "text-gray-700", bg: "bg-gray-100", icon: <XCircle className="w-4 h-4" /> }
};

export default function EssentialDocs() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [docs, setDocs] = useState<EssentialDoc[]>([]);
  const [stats, setStats] = useState<DocsResponse['stats'] | null>(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const [updating, setUpdating] = useState<string | null>(null);
  // expandedDoc state removed

  /**
   * What each checklist entry holds, keyed by entry id.
   *
   * Read straight from Firestore rather than through `getEssentialDocsV2`:
   * `firestore.rules` already grants members read on this subcollection
   * precisely so the checklist can show it, and widening the callable would
   * have been a server change and a deploy for something that is only display.
   */
  const [attachments, setAttachments] = useState<Record<string, TmfAttachmentLike[]>>({});
  const [downloading, setDownloading] = useState<string | null>(null);

  // uid → a name, so a filing reads "by Dana Reeve" rather than by a uid.
  const { members } = useProjectMembers(projectId);
  const people = useMemo(
    () =>
      Object.fromEntries(
        members.map(m => [m.userId, m.displayName || m.email || m.userId]),
      ),
    [members],
  );

  const categoryFilter = searchParams.get('category') || '';
  const zoneFilter = searchParams.get('zone') || '';
  const statusFilter = searchParams.get('status') || '';

  const functions = getFunctions();

  const loadDocs = async () => {
    setLoading(true);
    try {
      const getEssentialDocs = httpsCallable(functions, CLOUD_FUNCTIONS.GET_ESSENTIAL_DOCS);
      const result = await getEssentialDocs({ 
        projectId,
        category: categoryFilter || undefined,
        status: statusFilter || undefined
      });
      const data = result.data as DocsResponse;
      
      // Apply zone filter client-side if needed
      let filteredDocs = data.documents;
      if (zoneFilter) {
        filteredDocs = filteredDocs.filter(d => d.zone === zoneFilter);
      }
      
      setDocs(filteredDocs);
      setStats(data.stats);
      void loadAttachments(filteredDocs);
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch the files each entry holds.
   *
   * ONLY for entries that may hold one. The rest cannot have an attachment by
   * construction — `attachDocument` refuses them inside its transaction — so
   * reading their subcollection would spend a Firestore read to confirm an
   * empty result the row is not going to show anyway.
   *
   * Deliberately a one-shot `getDocs` rather than a listener, to match the way
   * this page already loads (a callable plus the Refresh button) and to avoid
   * standing up twenty live listeners for records only a callable ever writes.
   *
   * One entry failing does not fail the page: an entry whose read errors simply
   * shows as holding nothing, which is what it showed yesterday.
   */
  const loadAttachments = async (entries: EssentialDoc[]) => {
    if (!projectId) return;
    const holders = entries.filter(d => attachmentRefusal(d) === null);
    const pairs = await Promise.all(
      holders.map(async (d): Promise<[string, TmfAttachmentLike[]]> => {
        try {
          const snap = await getDocs(essentialDocAttachmentsCollection(projectId, d.id));
          return [d.id, snap.docs.map(s => ({ id: s.id, ...s.data() }) as TmfAttachmentLike)];
        } catch (err) {
          console.warn(`Could not read attachments for ${d.id}:`, err);
          return [d.id, []];
        }
      }),
    );
    setAttachments(Object.fromEntries(pairs));
  };

  /**
   * Download whatever the entry holds now.
   *
   * `versionId` is the literal `"current"` sentinel the endpoint accepts, so
   * the browser never has to name an attachment id — and cannot accidentally
   * ask for a file that has since been replaced.
   */
  const downloadHeldDocument = async (docId: string, versionId: string) => {
    setDownloading(docId);
    try {
      const fn = httpsCallable(functions, CLOUD_FUNCTIONS.GET_VERSION_DOWNLOAD_URL);
      const response = await fn({
        projectId,
        sourceDocFamily: 'essentialDoc',
        sourceDocId: docId,
        versionId,
      });
      const data = response.data as { url: string; filename: string | null };
      // The signed URL is cross-origin, where the `download` attribute is
      // ignored — the filename is signed into the URL server-side instead.
      const link = document.createElement('a');
      link.href = data.url;
      if (data.filename) link.download = data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Could not download this document', 'error');
    } finally {
      setDownloading(null);
    }
  };

  const updateStatus = async (docId: string, newStatus: string) => {
    setUpdating(docId);
    try {
      const updateEssentialDocStatus = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_ESSENTIAL_DOC_STATUS);
      await updateEssentialDocStatus({ projectId, documentId: docId, status: newStatus });
      await loadDocs();
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Failed to update status', 'error');
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    loadDocs();
  }, [projectId, categoryFilter, statusFilter, zoneFilter]);

  const clearFilters = () => {
    setSearchParams({});
  };

  const setFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  // Group docs by category
  const groupedDocs = docs.reduce((acc, doc) => {
    if (!acc[doc.category]) acc[doc.category] = [];
    acc[doc.category].push(doc);
    return acc;
  }, {} as Record<string, EssentialDoc[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Back to the Documents & TMF step, not to this page — the arrow
                pointed at its own route and did nothing. */}
            <Link to={`/project/${projectId}/documents`} className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">Essential Documents</h1>
            </div>
          </div>
          <button
            onClick={loadDocs}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4 shadow text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow text-center">
              <p className="text-2xl font-bold text-green-600">{stats.complete}</p>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
              <p className="text-xs text-gray-500">In Progress</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow text-center">
              <p className="text-2xl font-bold text-red-600">{stats.required}</p>
              <p className="text-xs text-gray-500">Not Started</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow text-center">
              <p className="text-2xl font-bold text-gray-600">{stats.notApplicable}</p>
              <p className="text-xs text-gray-500">N/A</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <select
              value={categoryFilter}
              onChange={(e) => setFilter('category', e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              <option value="">All Categories</option>
              <option value="before_trial">Before Trial</option>
              <option value="during_trial">During Trial</option>
              <option value="after_trial">After Trial</option>
            </select>

            <select
              value={zoneFilter}
              onChange={(e) => setFilter('zone', e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              <option value="">All Zones</option>
              {Object.entries(zoneLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setFilter('status', e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm"
            >
              <option value="">All Statuses</option>
              <option value="required">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
              <option value="not_applicable">N/A</option>
            </select>

            {(categoryFilter || zoneFilter || statusFilter) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Documents List */}
        {Object.entries(groupedDocs).length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            No documents found matching your filters.
          </div>
        ) : (
          Object.entries(groupedDocs).map(([category, categoryDocs]) => (
            <div key={category} className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                {category === 'before_trial' && <Clock className="w-5 h-5 text-blue-600" />}
                {category === 'during_trial' && <FileText className="w-5 h-5 text-purple-600" />}
                {category === 'after_trial' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                {categoryLabels[category]}
                <span className="text-sm font-normal text-gray-500">({categoryDocs.length})</span>
              </h2>
              
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="divide-y">
                  {categoryDocs.map((doc) => {
                    // What this entry holds — or why it may not hold anything.
                    // The refusal is decided from the entry itself, before the
                    // attachments are looked at, exactly as the server does it.
                    const holding = describeHolding(doc, attachments[doc.id] ?? [], { people });

                    return (
                    <div key={doc.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-medium text-gray-900">{doc.name}</h3>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[doc.status]?.bg} ${statusConfig[doc.status]?.color}`}>
                              {statusConfig[doc.status]?.icon}
                              {statusConfig[doc.status]?.label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{doc.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {zoneLabels[doc.zone] || doc.zone}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {locatedInLabel(doc.locatedIn)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <select
                            value={doc.status}
                            onChange={(e) => updateStatus(doc.id, e.target.value)}
                            disabled={updating === doc.id}
                            className={`px-3 py-1.5 border rounded-lg text-sm ${updating === doc.id ? 'opacity-50' : ''}`}
                          >
                            <option value="required">Not Started</option>
                            <option value="in_progress">In Progress</option>
                            <option value="complete">Complete</option>
                            <option value="not_applicable">N/A</option>
                          </select>
                        </div>
                      </div>

                      {/* What this entry holds.
                          Three states, and every one of them says something: a
                          silent row, or a disabled button with no explanation,
                          reads as a bug and gets reported as one. */}
                      <div className="mt-3">
                        {holding.state === 'holds' && (
                          <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className="text-sm font-medium text-gray-900 break-all">
                                  {holding.filename}
                                </span>
                                {holding.versionLabel && (
                                  <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                    {holding.versionLabel}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1 ml-6">{holding.detail}</p>
                              {holding.earlierVersionNote && (
                                <p className="text-xs text-gray-400 mt-0.5 ml-6">
                                  {holding.earlierVersionNote}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => holding.downloadVersionId &&
                                downloadHeldDocument(doc.id, holding.downloadVersionId)}
                              disabled={downloading === doc.id}
                              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                            >
                              {downloading === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                              Download
                            </button>
                          </div>
                        )}

                        {holding.state === 'empty' && (
                          <div className="rounded-lg border border-dashed border-gray-300 px-3 py-2">
                            <p className="text-xs text-gray-500">{holding.detail}</p>
                          </div>
                        )}

                        {holding.state === 'refused' && (
                          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                            <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800">{holding.reason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}

